/**
 * FIManagerCard Component
 *
 * Main container for the F&I Manager Performance dashboard. Mirrors
 * SalespersonCard but shows back-end gross production per F&I manager.
 * Retail deals only — wholesale and dealer-swap deals are excluded.
 */

import React from 'react';
import {
  BanknotesIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner } from '../../LoadingSpinner';
import { FiManager, FiManagerSummary, SortConfig } from './types';
import { FIManagerList } from './FIManagerList';
import { FIManagerPieChart } from './FIManagerPieChart';

interface FIManagerCardProps {
  fiManagers: FiManager[];
  summary: FiManagerSummary | null;
  loading: boolean;
  refreshing?: boolean;
  error: string | null;
  year: number;
  month: number;
  sortConfig: SortConfig;
  onSortChange: (config: SortConfig) => void;
  onRefresh?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

function getMonthName(month: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return months[month - 1] || '';
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export const FIManagerCard: React.FC<FIManagerCardProps> = ({
  fiManagers,
  summary,
  loading,
  refreshing = false,
  error,
  year,
  month,
  sortConfig,
  onSortChange,
  onRefresh,
  collapsed = false,
  onToggleCollapse,
}) => {
  // Summary FI totals are optional (legacy records) — fall back to reducing rows.
  const totalBackEndGross = summary?.totalBackEndGross ??
    fiManagers.reduce((sum, mgr) => sum + mgr.backEndGross, 0);
  const totalRetailUnits = summary?.totalRetailUnits ??
    fiManagers.reduce((sum, mgr) => sum + mgr.totalUnits, 0);
  const totalServiceContracts = summary?.totalServiceContracts ??
    fiManagers.reduce((sum, mgr) => sum + mgr.serviceContractCount, 0);
  const overallPvr = totalRetailUnits > 0 ? totalBackEndGross / totalRetailUnits : null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div
        className={`px-4 py-3 ${collapsed ? '' : 'border-b border-gray-200'} ${onToggleCollapse ? 'cursor-pointer select-none hover:bg-gray-50 transition-colors' : ''}`}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BanknotesIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                F&amp;I Manager Performance
              </h3>
              <p className="text-sm text-gray-500">
                {getMonthName(month)} {year} &middot; Retail deals only
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {!collapsed && onRefresh && (
              <button
                onClick={(e) => { e.stopPropagation(); onRefresh(); }}
                disabled={loading || refreshing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                title="Refresh F&I manager data from DealerBuilt"
              >
                <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            )}
            {!collapsed && fiManagers.length > 0 && (
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(totalBackEndGross)}
                </p>
                <p className="text-sm text-gray-500">
                  {totalRetailUnits} Units
                  {overallPvr !== null && <> &middot; PVR {formatCurrency(overallPvr)}</>}
                  {' '}&middot; {totalServiceContracts} SCs
                </p>
              </div>
            )}
            {onToggleCollapse && (
              <ChevronDownIcon
                className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
              />
            )}
          </div>
        </div>
      </div>

      {!collapsed && <div className="p-4">
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <LoadingSpinner />
            <p className="mt-4 text-gray-600">Loading F&amp;I manager data...</p>
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-red-800 font-medium">Error Loading Data</h4>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && (
          <>
            {fiManagers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <BanknotesIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p>No F&amp;I manager data available for this period.</p>
                <p className="text-sm mt-1">
                  Data is loaded nightly from DealerBuilt.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <FIManagerList
                    fiManagers={fiManagers}
                    sortConfig={sortConfig}
                    onSortChange={onSortChange}
                  />
                </div>
                <div className="lg:col-span-1">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 text-center">
                      Back End Gross Distribution
                    </h4>
                    <FIManagerPieChart
                      fiManagers={fiManagers}
                      totalBackEndGross={totalBackEndGross}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>}

      {!collapsed && summary?.lastUpdated && !loading && (
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg">
          <p className="text-xs text-gray-500 text-center">
            Last updated:{' '}
            {new Date(summary.lastUpdated).toLocaleString('en-US', {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </p>
        </div>
      )}
    </div>
  );
};
